import { isArray } from './array.utils';

/**
 * Check given argument is a non-empty object.
 * Definition for non-emtpy object: an object with at least 1 enumerable property
 * in its chain, either own or inherited from the prototype.
 *
 * Implementation of this function is aimed towards performance:
 * https://stackoverflow.com/questions/679915/how-do-i-test-for-an-empty-javascript-object/59787784#59787784
 */
export const isNonEmptyObject = (a: unknown): boolean => {
  if (a && typeof a === 'object') {
    // eslint-disable-next-line guard-for-in
    for (const _ in a) {
      return true;
    }
  } // Not using hasOwnProperty is intentional here
  return false;
};

/**
 * [IMMUTABLE]
 * Helper to map object own properties in a RECURSIVE way.
 * @param source Source object whose properties are going to be mapped
 * @param mapCallback Callback that comprises the mapping
 * @param target Target object where the mapping must be leveraged
 * @returns The target object
 */
export const mapObjectOwnPropsRecursive = <S extends object, T extends object = S>(
  source: S,
  mapCallback: (target, key, value) => void,
  target: T = {} as T
): S & T => {
  for (const key in source) {
    if (Object.hasOwn(source, key)) {
      const value = source[key];
      if (typeof value === 'object' && value !== null) {
        const newTarget = ((target as unknown)[key] = isArray(value) ? [] : {});
        mapObjectOwnPropsRecursive(value as S, mapCallback, newTarget);
      } else {
        mapCallback(target as S & T, key, value);
      }
    }
  }
  return target as S & T;
};

/**
 * [IMMUTABLE]
 * Helper to map object first-level-only own properties.
 * @param source Source object whose first-level properties are going to be mapped
 * @param mapCallback Callback that comprises the mapping
 * @returns The mapped object
 */
export const mapObjectOwnValues = <S extends object, T extends object = S>(
  source: S,
  mapCallback: (value: S[keyof S], key?: keyof S) => unknown
): T => {
  const target = {} as S;
  for (const key in source) {
    if (Object.hasOwn(source, key)) {
      const value = source[key];
      (target as unknown)[key] = mapCallback(value, key);
    }
  }
  return target as unknown as T;
};

/**
 * [IMMUTABLE]
 * Given an input object, it filters its own ennumerable properties by a
 * given callback. The callback is called for each own ennumerable property
 * of the object.
 * @param obj Input object
 * @param callback Predicate or filter callback
 * @returns A new filtered object
 */
export const filterObjectOwnProperties = <
  T extends object,
  C extends (value: [keyof T, T[keyof T]], index?: number) => boolean,
>(
  obj: T,
  callback: C = (([, value]) => value !== undefined && value !== null) as C
): T =>
  Object.fromEntries(
    Object.entries(obj ?? {}).filter(callback as (value: [string, unknown]) => boolean)
  ) as T;

/**
 * [IMMUTABLE]
 * Helper to iterate over object own props in a RECURSIVE way.
 * @param source Source object whose properties are going to be iterated
 * @param callback Callback triggered for each iteration
 */
export const iterateObjectOwnPropsRecursive = <S extends object>(
  source: S,
  callback: (key, value) => void
): void => {
  for (const key in source) {
    if (Object.hasOwn(source, key)) {
      const value = source[key];
      if (typeof value === 'object' && value !== null) {
        iterateObjectOwnPropsRecursive(value as S, callback);
      } else {
        callback(key, value);
      }
    }
  }
};
